// src/components/ChatPanel.jsx
import React, { useEffect, useState, useRef, Fragment } from 'react'
import { Tab, Transition } from '@headlessui/react'
import { FiX, FiSend, FiSearch, FiMessageSquare } from 'react-icons/fi'
import SelectDropdown from '../components/SelectDropdown'
import { useSelector } from 'react-redux'
import { db } from '../firebase'
import request from '../utils/request'
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
    getDocs,
    getDoc,
    orderBy,
    increment,
    writeBatch,
    setDoc
} from 'firebase/firestore'
import useLockBodyScroll from '../utils/useLockBodyScroll'
import { getOrCreateConvo } from '../utils/chat'
import { now } from 'moment'
import moment from 'moment-timezone'
import sound from '../sounds/notify.mp3'

export default function ChatPanel({ isOpen, onClose, order, drivers = [] }) {
    useLockBodyScroll(isOpen);
    const { adminInfo } = useSelector(s => s.login)
    const [conversations, setConversations] = useState([])
    const [selectedConv, setSelectedConv] = useState(null)
    const [messages, setMessages] = useState([])
    const [tabIndex, setTabIndex] = useState(0)
    const [searchTerm, setSearchTerm] = useState('')
    const [newChatOpen, setNewChatOpen] = useState(false)
    const [selectedDriver, setSelectedDriver] = useState(null)
    const [newMessage, setNewMessage] = useState('')
    const [isDriverTyping, setIsDriverTyping] = useState(false)
    const scrollRef = useRef()
    const driversList = drivers.map(d => ({ value: d.id, label: d.name }))
    const selectedConvRef = useRef(null);
    // At top of ChatPanel()
    // At the top of ChatPanel()
    const audioRef = useRef(new Audio(sound));


    useEffect(() => {
        // whenever the panel closes, clear the last‐selected convo
        if (!isOpen && selectedConvRef.current) {
            const id = selectedConvRef.current.id;
            updateDoc(doc(db, 'conversations', id), { unreadCount: 0 })
                .catch(err => console.error('Failed to clear unread on close:', err));
        }
    }, [isOpen]);



    useEffect(() => {
        // request permission on first user click
        function enableNotifications() {
            if ('Notification' in window) {
                Notification.requestPermission().then(perm => {
                    console.log('Notification permission:', perm);
                }).catch(console.error);
            }
            window.removeEventListener('click', enableNotifications);
        }
        window.addEventListener('click', enableNotifications);
    }, []);


    useEffect(() => { selectedConvRef.current = selectedConv }, [selectedConv]);

    useEffect(() => {
        if (!selectedConv) return
        updateDoc(doc(db, 'conversations', selectedConv.id), {
            unreadCount: 0
        }).catch(err => {
            console.error('Failed to clear unread count:', err)
        })
    }, [selectedConv])

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);
    function handleDriverSelect(opt) {
        if (!opt) return setSelectedDriver(null)
        if (typeof opt === 'string') {
            const byValue = driversList.find(o => o.value === opt)
            const byLabel = driversList.find(o => o.label === opt)
            return setSelectedDriver(byValue || byLabel || null)
        }
        if (opt.value && opt.label) return setSelectedDriver(opt)
        if (opt.id && opt.name) return setSelectedDriver({ value: opt.id, label: opt.name })
        setSelectedDriver(null)
    }

    // 1) subscribe to all conversations
    useEffect(() => {
        if (!isOpen) return
        const q = query(
            collection(db, 'conversations'),
            where('participants', 'array-contains', adminInfo._id)
        )
        const unsub = onSnapshot(q, snap => {
            const convs = snap.docs.map(d => {
                const data = d.data()
                const parts = data.participants || []
                const peerID = parts.find(p => drivers.some(d => d.id === p))
                return {
                    id: d.id,
                    participants: parts,
                    peerID,
                    orderID: data.orderID,
                    lastMessage: data.lastMessage,
                    updatedAt: data.updatedAt,
                    unreadCount: data.unreadCount || 0
                }
            })
            snap.docChanges().forEach(change => {

            });


            // client‐side sort by updatedAt desc
            convs.sort((a, b) => {
                const aMs = a.updatedAt?.toDate?.().getTime() || 0
                const bMs = b.updatedAt?.toDate?.().getTime() || 0
                return bMs - aMs
            })
            setConversations(convs)
        })
        return () => unsub()
    }, [isOpen, adminInfo._id])

    // 2) stream messages
    useEffect(() => {
        if (!selectedConv) return;
        const msgsQ = query(
            collection(db, 'messages'),
            where('conversationId', '==', selectedConv.id),
            orderBy('createdAt', 'asc')
        );
        return onSnapshot(msgsQ, snap => {
            setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [selectedConv]);

    // 2) NEW: listen to _all_ messages and bump unreadCount for closed convos
    useEffect(() => {

        const allMsgsQ = query(collection(db, 'messages'), orderBy('createdAt', 'asc'));
        let initialized = false;
        const unsub = onSnapshot(allMsgsQ, snap => {
            if (!initialized) {
                initialized = true;
                return;  // skip Firestore’s initial “added” batch
            }
            snap.docChanges().forEach(change => {
                const data = change.doc.data();
                if (
                    change.type === 'added' &&
                    data.user._id !== adminInfo._id &&
                    data.conversationId !== selectedConvRef.current?.id
                ) {
                    updateDoc(doc(db, 'conversations', data.conversationId), {
                        unreadCount: increment(1)
                    }).catch(console.error);

                    // inside the “docChanges” loop, replace your Notification block

                    if (Notification.permission === 'granted') {
                        navigator.serviceWorker.getRegistration().then(reg => {
                            if (reg) {
                                // use the SW API — always shows, focused or not
                                reg.showNotification('New message', { body: data.text });
                            } else {
                                // fallback (shouldn’t happen once the worker is registered)
                                new Notification('New message', { body: data.text });
                            }
                        }).catch(console.error);
                    }



                    // play sound
                    if (audioRef.current) {
                        console.log('Playing notification sound');
                        audioRef.current.play().catch(err => {
                            console.warn('Audio play failed:', err);
                        });
                    }
                }
            });
        });
        return () => unsub();
    }, [adminInfo._id, isOpen]);
    // const clearChatsForDriver = async () => {
    //     if (!selectedDriver?.value) {
    //         return alert('Select a driver first.')
    //     }
    //     const driverId = selectedDriver.value
    //     if (
    //         !window.confirm(
    //             'This will permanently delete ALL chats with this driver. Continue?'
    //         )
    //     ) return

    //     // 1) find all conversations involving this driver
    //     const convQ = query(
    //         collection(db, 'conversations'),
    //         //   where('participants', 'array-contains', driverId)
    //     )
    //     const convSnap = await getDocs(convQ)

    //     // 2) for each convo, delete its messages then the convo itself
    //     for (const convDoc of convSnap.docs) {
    //         const convoId = convDoc.id
    //         // delete all messages in that convo
    //         const msgQ = query(
    //             collection(db, 'messages'),
    //             where('conversationId', '==', convoId)
    //         )
    //         const msgSnap = await getDocs(msgQ)

    //         const batch = writeBatch(db)
    //         msgSnap.docs.forEach(m => batch.delete(m.ref))
    //         batch.delete(convDoc.ref)
    //         await batch.commit()
    //     }

    //     alert(`All chats with driver ${driverId} have been deleted.`)
    //     setSelectedConv(null)
    // }


    // 3a) start/reuse order chat
    const startOrderChat = async () => {
        if (!order?._id || !selectedDriver?.value) return
        const driverId = selectedDriver.value



        // 1) find-or-create convo
        const convoRef = await getOrCreateConvo(adminInfo._id, driverId, order._id)
        const snap = await getDoc(convoRef)
        const data = snap.data()
        console.log("createConvo returned ref:", convoRef.id)

        // 2) select it in UI
        setNewChatOpen(false)
        setSelectedConv({
            id: convoRef.id,
            participants: data.participants,
            peerID: driverId,
            orderID: data.orderID
        })

        // 3) prime with a starter message
        const user = { _id: adminInfo._id, name: adminInfo.firstName, avatar: adminInfo.profilePicture || '' }
        const createdAt = serverTimestamp()
        const date = moment(selectedConv.createdAt.toDate()).tz('America/Los_Angeles')           // or your zone.format('MMMM Do, YYYY h:mm A z')

        await addDoc(collection(db, 'messages'), {
            conversationId: convoRef.id,
            text: `Chat started ${date}`,
            user,
            createdAt,
            isRead: false
        })

        // 4) bump the convo’s lastMessage & createdAt
        await updateDoc(doc(db, 'conversations', convoRef.id), {
            lastMessage: `Chat started ${date}`,
            createdAt,
        })
    }

    // 3b) start/reuse general chat
    const startGeneralChat = async () => {
        if (!selectedDriver?.value) return
        const driverId = selectedDriver.value

        // 1) find-or-create with null orderID
        const convoRef = await getOrCreateConvo(adminInfo._id, driverId, null)


        // 3) select it
        const snap = await getDoc(convoRef)
        const data = snap.data()
        setNewChatOpen(false)
        setSelectedConv({
            id: convoRef.id,
            participants: data.participants,
            peerID: driverId,
            orderID: data.orderID,
            propertyAddress: data.propertyAddress,
            orderDeliveryDate: data.orderDeliveryDate,
            storeLocation: data.storeLocation
        })

        // 4) prime with starter message
        const user = { _id: adminInfo._id, name: adminInfo.firstName, avatar: adminInfo.profilePicture || '' }
        const createdAt = serverTimestamp()
        const date = moment(now()).tz('America/Los_Angeles').format('MMMM Do, YYYY h:mm A z')

        await addDoc(collection(db, 'messages'), {
            conversationId: convoRef.id,
            text: `Chat started ${date}`,
            user,
            createdAt,
            isRead: false
        })

        // 5) bump convo
        await updateDoc(doc(db, 'conversations', convoRef.id), {
            lastMessage: `Chat started ${date}`,
            createdAt
        })
    }

    // 4) send message + link it
    const sendMessage = async () => {
        const text = newMessage.trim()
        if (!text || !selectedConv) {
            return alert('Select a conversation and type a message.')
        }
        setNewMessage('')

        const user = { _id: adminInfo._id, name: adminInfo.firstName, avatar: adminInfo.profilePicture || '' }
        const createdAt = serverTimestamp()

        try {
            // write message
            await addDoc(collection(db, 'messages'), {
                conversationId: selectedConv.id,
                text,
                user,
                createdAt,
                isRead: false
            })

            // update convo
            await updateDoc(doc(db, 'conversations', selectedConv.id), {
                lastMessage: text,
                updatedAt: createdAt,
                // ← this flags it for the other side
            })
            console.log("sending message to convo:", selectedConv.id, newMessage)


            // push notification
            await request.post('/admin/send-push-notification', {
                driverId: selectedConv.peerID,
                body: text,
                title: selectedConv.orderID ? `Order #${selectedConv.orderID}` : 'New Message'
            })
        } catch (err) {
            console.error('Firestore write failed:', err)
            setNewMessage(text)
        }
    }
    // 5) filter client‐side
 const filtered = conversations.filter(c => {
   // guard peerID → default to empty string
   const name = (c.peerID || '').toLowerCase();
   return name.includes(searchTerm.toLowerCase());
 });

    const byDriver = filtered.reduce((acc, conv) => {
        if (!acc[conv.peerID]) acc[conv.peerID] = [];
        acc[conv.peerID].push(conv);
        return acc;
    }, {});


    const convsByDriver = filtered.reduce((acc, conv) => {
        const key = conv.peerID;
        const existing = acc[key];
        if (!existing) {
            acc[key] = conv;
        } else {
            // prefer the general (orderID === null)
            if (conv.orderID === null && existing.orderID !== null) {
                acc[key] = conv;
            } else if (conv.orderID !== null && existing.orderID === null) {
                // keep existing general
            } else {
                // otherwise pick the most recently updated
                const t1 = conv.updatedAt?.toDate().getTime() || 0;
                const t2 = existing.updatedAt?.toDate().getTime() || 0;
                if (t1 > t2) acc[key] = conv;
            }
        }
        return acc;
    }, {});
    const merged = Object.values(convsByDriver);


    if (!isOpen) return null


    const lookupName = id => drivers.find(d => d.id === id)?.name || id
    function handlePanelClose() {
        if (selectedConv) {
            updateDoc(doc(db, 'conversations', selectedConv.id), {
                unreadCount: 0
            }).catch(console.error);
            setSelectedConv(null);
        }
        onClose();
    }


    return (
        <Transition show={isOpen} as={Fragment}>
            <div className="fixed inset-y-0 right-0 z-50 flex h-screen w-3/4 bg-white rounded-l-xl shadow-[0_4px_16px_rgba(0,0,0,0.05)] pointer-events-auto">
                {/* Sidebar */}
                <div className="w-96 border-r flex flex-col min-h-0 overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
                    {/* header */}
                    <div className="h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: '#E5E7EB' }}>
                        <h3 className="text-xl font-semibold text-[#111827]">Chats</h3>
                        <button onClick={handlePanelClose} className="p-2 rounded-full hover:bg-[#F3F4F6]">
                            <FiX className="w-6 h-6 text-[#6B7280]" />
                        </button>
                    </div>
                    {/* + New Chat */}
                    <div className="p-4 bg-[#F3F4F6] border-b" style={{ borderColor: '#E5E7EB' }}>
                        {/* <button
                            onClick={clearChatsForDriver}
                            disabled={!selectedDriver}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50"
                        >
                            Clear Chats
                        </button> */}
                        <button
                            onClick={() => setNewChatOpen(true)}
                            className="w-full bg-[#0A84FF] text-white rounded-lg px-4 py-2"
                        >+ New Chat</button>
                    </div>
                    {newChatOpen && (
                        <div className="p-4 bg-[#F3F4F6] border-b space-y-3" style={{ borderColor: '#E5E7EB' }}>
                            <SelectDropdown
                                placeholder="Select driver…"
                                options={driversList}
                                value={selectedDriver}
                                onChange={handleDriverSelect}
                            />
                            <div className="flex space-x-2 justify-end">
                                <button
                                    onClick={tabIndex === 1 ? startOrderChat : startGeneralChat}
                                    disabled={!selectedDriver}
                                    className="bg-[#10B981] text-white px-4 py-2 rounded-lg disabled:opacity-50"
                                >
                                    {tabIndex === 1 ? 'Start Order Chat' : 'Start General Chat'}
                                </button>
                                <button
                                    onClick={() => setNewChatOpen(false)}
                                    className="bg-[#D1D5DB] px-4 py-2 rounded-lg hover:bg-[#E5E7EB]"
                                >Cancel</button>
                            </div>
                        </div>
                    )}
                    {/* search + tabs */}
                    <div className="px-6 py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
                        <div className="relative mb-3">
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input
                                type="search"
                                placeholder="Search…"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0A84FF]"
                                style={{ borderColor: '#D1D5DB' }}
                            />
                        </div>
                        <Tab.Group selectedIndex={tabIndex} onChange={setTabIndex}>
                            <Tab.List className="flex space-x-2">
                                {['All', 'Order-linked', 'General'].map((lbl, i) => (
                                    <Tab key={i} className={({ selected }) =>
                                        `px-3 py-1 rounded-full text-sm font-medium ${selected ? 'bg-black text-white' : 'bg-[#E5E7EB] text-[#6B7280]'
                                        }`
                                    }>{lbl}</Tab>
                                ))}
                            </Tab.List>
                        </Tab.Group>
                    </div>
                    {/* conversation list */}
                    <div className="flex-1 overflow-auto p-4 space-y-4">
                        {merged.map(c => (
                            <div
                                key={c.peerID}
                                onClick={() => setSelectedConv(c)}
                                className={`
                                    flex items-center justify-between p-3 bg-white rounded-xl
                                    shadow-sm hover:shadow-md cursor-pointer
                                    ${selectedConv?.peerID === c.peerID ? 'ring-2 ring-[#0A84FF]' : ''}
                                `}
                            >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                    <div className="w-10 h-10 bg-[#F3F4F6] rounded-full flex items-center justify-center">
                                        <FiMessageSquare className="w-6 h-6 text-[#9CA3AF]" />
                                    </div>
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                        <p className="text-base font-medium text-[#111827]">
                                            {lookupName(c.peerID)}
                                        </p>
                                        <p className="text-xs text-[#6B7280] truncate">
                                            {c.lastMessage}
                                        </p>
                                    </div>
                                </div>
                                {c.unreadCount > 0 && (
                                    <span className="bg-[#EF4444] text-white text-xs rounded-full px-2">
                                        {c.unreadCount}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                </div>

                {/* Active Chat */}
                <div className="flex-1 flex flex-col min-h-0">
                    {selectedConv ? (
                        <Fragment>
                            {/* header */}
                            <div className="h-16 flex items-center justify-between px-6 border-b" style={{ borderColor: '#E5E7EB' }}>
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-[#F3F4F6] rounded-full flex items-center justify-center">
                                        <FiMessageSquare className="w-6 h-6 text-[#9CA3AF]" />
                                    </div>
                                    <div>
                                        <p className="text-base font-medium text-[#111827]">{lookupName(selectedConv.peerID)}</p>

                                    </div>
                                </div>
                                <button onClick={handlePanelClose} className="p-2 rounded-full hover:bg-[#F3F4F6]">
                                    <FiX className="w-6 h-6 text-[#6B7280]" />
                                </button>
                            </div>
                            {/* messages */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {messages.map(m => (
                                    <div key={m.id}
                                        className={`max-w-[75%] p-3 rounded-xl shadow-sm ${m.user._id === adminInfo._id
                                            ? 'ml-auto bg-[#0A84FF] text-white'
                                            : 'bg-[#F3F4F6] text-[#111827]'
                                            }`}
                                    >
                                        <p className="text-l">{m.text}</p>
                                        <span className="text-sm text-gray block mt-1 text-right">
                                            {(() => {
                                                if (m.createdAt?.toDate) return m.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                if (m.createdAt instanceof Date) return m.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                return ''
                                            })()}
                                        </span>
                                    </div>
                                ))}
                                <div ref={scrollRef} />
                            </div>
                            {isDriverTyping && (
                                <p className="px-6 text-xs italic text-[#6B7280] mb-2">Driver is typing…</p>
                            )}
                            {/* composer */}
                            <div className="px-6 py-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                                <div className="flex items-center space-x-3">
                                    <textarea
                                        rows={1}
                                        value={newMessage}
                                        onChange={e => setNewMessage(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();       // ← stop the browser from inserting a newline
                                                sendMessage();            // ← this will setNewMessage('') at the top
                                            }
                                        }}
                                        placeholder="Type a message…"
                                        className="flex-1 px-4 py-2 border rounded-lg resize-none text-sm focus:outline-none"
                                        style={{ borderColor: '#D1D5DB' }}
                                    />
                                    <button onClick={sendMessage} className="p-2 rounded-full hover:bg-[#F3F4F6]">
                                        <FiSend className="w-5 h-5 text-[#0A84FF]" />
                                    </button>
                                </div>
                            </div>
                        </Fragment>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-[#6B7280]">
                            Select a conversation to start chatting
                        </div>
                    )}
                </div>
            </div>
        </Transition>
    )

}

